import { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../axiosInstance";

// Keys must match CRM StudentFile::FILE_TYPES.
const DOC_TYPES = [
  { value: "copy_of_passport", label: "copyOfPassport" },
  { value: "student_letter", label: "studentLetter" },
  { value: "proof_of_payment", label: "proofOfPayment" },
  { value: "other", label: "other" },
];

const STATUS_COLORS = { accepted: "success", declined: "error", review: "warning" };

const isImage = (name) => /\.(jpe?g|png|gif|webp|bmp)$/i.test(name || "");
const isPdf = (name) => /\.pdf$/i.test(name || "");

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
  XRefreshToken: `${localStorage.getItem("loginUser")}`,
});

export default function StudentDocuments({ studentId }) {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState(DOC_TYPES[0].value);
  const [preview, setPreview] = useState(null);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}auth/students/${studentId}/files`;
      const res = await axiosInstance.get(url, { headers: authHeaders() });
      setFiles(res?.data?.files || []);
    } catch (err) {
      console.error("Error loading documents:", err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  // PDFs are not previewed in the dialog; open them in a new tab.
  const openFile = (f) => {
    if (!f.url) return;
    if (isPdf(f.file_name)) window.open(f.url, "_blank", "noopener,noreferrer");
    else setPreview(f);
  };

  const upload = async (file, type, replaceFileId) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("student_id", studentId);
      formData.append("file", file);
      formData.append("file_note", "");
      formData.append("doc_type", type);
      if (replaceFileId) formData.append("replace_file_id", replaceFileId);
      const url = `${process.env.REACT_APP_BASE_URL}auth/upload_file`;
      const res = await axiosInstance.post(url, formData, { headers: authHeaders() });
      if (!res?.data?.success) throw new Error(res?.data?.message || "Upload failed");
      await fetchFiles();
    } catch (err) {
      Swal.fire({
        icon: "error",
        text: err?.response?.data?.message || err?.message || "Upload failed",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ opacity: uploading ? 0.5 : 1, pointerEvents: uploading ? "none" : "auto" }}>
      <Typography variant="subtitle1" gutterBottom>
        {t("documents")}
      </Typography>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>{t("documentType")}</InputLabel>
          <Select
            value={docType}
            label={t("documentType")}
            onChange={(e) => setDocType(e.target.value)}
          >
            {DOC_TYPES.map((d) => (
              <MenuItem key={d.value} value={d.value}>
                {t(d.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          component="label"
          variant="contained"
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
          sx={{ textTransform: "capitalize", borderRadius: "1rem" }}
        >
          {t("upload")}
          <input
            hidden
            type="file"
            accept=".webp,.jpg,.jpeg,.png,.pdf,.doc,.docx"
            onChange={(e) => {
              upload(e.target.files[0], docType);
              e.target.value = "";
            }}
          />
        </Button>
      </Box>

      {loading ? (
        <CircularProgress size={20} />
      ) : (
        files.map((f) => (
          <Paper
            key={f.file_id}
            variant="outlined"
            sx={{ p: 1.5, mb: 1.5, borderRadius: "1rem", display: "flex", gap: 1.5, alignItems: "center" }}
          >
            <Avatar
              variant="rounded"
              src={isImage(f.file_name) ? f.url : undefined}
              onClick={() => openFile(f)}
              sx={{ width: 48, height: 48, cursor: "pointer" }}
            >
              <InsertDriveFileIcon />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2">{f.file_type}</Typography>
              <Typography
                variant="caption"
                onClick={() => openFile(f)}
                sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", textDecoration: "underline" }}
              >
                {f.file_name}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                <Chip
                  size="small"
                  label={t(f.status)}
                  color={STATUS_COLORS[f.status] || "default"}
                  onClick={() => openFile(f)}
                  sx={{ textTransform: "capitalize" }}
                />
                {f.created_at ? (
                  <Chip size="small" variant="outlined" label={dayjs(f.created_at).format("YYYY-MM-DD")} />
                ) : null}
              </Box>
            </Box>
            <Button component="label" size="small" sx={{ textTransform: "capitalize" }}>
              {t("change")}
              <input
                hidden
                type="file"
                accept=".webp,.jpg,.jpeg,.png,.pdf,.doc,.docx"
                onChange={(e) => {
                  upload(e.target.files[0], f.doc_type, f.file_id);
                  e.target.value = "";
                }}
              />
            </Button>
          </Paper>
        ))
      )}

      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>{preview?.file_type}</DialogTitle>
        <DialogContent dividers sx={{ display: "flex", justifyContent: "center" }}>
          {isImage(preview?.file_name) ? (
            <Box component="img" src={preview?.url} alt={preview?.file_name} sx={{ maxWidth: "100%", maxHeight: "70vh" }} />
          ) : (
            <Typography>{preview?.file_name}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button href={preview?.url} target="_blank" rel="noopener noreferrer" sx={{ textTransform: "capitalize" }}>
            {t("open")}
          </Button>
          <Button onClick={() => setPreview(null)} sx={{ textTransform: "capitalize" }}>
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
